import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { Logo } from '../../Logo';
import { NotificationIcon, ToggleIcon, SearchIcon } from '../../icons/DashboardIcons';
import { colors, typography } from '../../../styles/theme/index';
import theme from '../../../styles/theme/index';

const PageWrapper = styled.div`
  min-height: 100vh;
  display: flex;
`;

const Sidebar = styled.nav`
  width: ${props => props.isCollapsed ? '0px' : '250px'};
  background: white;
  border-right: 0px solid ${colors.border};
  padding: 24px 16px;
  display: flex;
  flex-direction: column;
  transition: width 0.3s ease;
  position: relative;
`;

const MainContent = styled.main`
  flex: 1;
  background: #f1f7f3;
`;

const LogoWrapper = styled.div`
  margin-bottom: 60px;
  display: ${props => props.isCollapsed ? 'none' : 'flex'};
  justify-content: ${props => props.isCollapsed ? 'center' : 'flex-start'};
  overflow: hidden;
  height: ${props => props.isCollapsed ? '0' : 'auto'};
  margin: ${props => props.isCollapsed ? '0' : '0 0 60px 0'};
`;

const NavMenu = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  position: absolute;
  top: 142px;
  left: 16px;
  right: 16px;
  opacity: ${props => props.isCollapsed ? 0 : 1};
  visibility: ${props => props.isCollapsed ? 'hidden' : 'visible'};
  transition: opacity 0.2s ease, visibility 0.2s ease;
`;

const NavItem = styled.a`
  padding: 12px 16px;
  border-radius: 8px;
  color: ${props => (props.active && !props.isCollapsed) ? colors.primary : colors.text.secondary};
  background: ${props => (props.active && !props.isCollapsed) ? colors.accent + '20' : 'transparent'};
  font-family: ${typography.fontFamily};
  font-size: ${typography.fontSize.base};
  text-decoration: none;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: flex;
  align-items: center;
  pointer-events: ${props => props.isCollapsed ? 'none' : 'auto'};

  &:hover {
    background: ${colors.accent}20;
    color: ${colors.primary};
  }
`;

const ToggleButton = styled.button`
  position: absolute;
  right: 0px;
  top: 450px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: white;
  border: 0px solid ${colors.border};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${colors.text.secondary};
  padding: 0;
  transition: all 0.2s ease;

  
`;

const TitleBar = styled.div`
  background: #0A5953;
  padding: 24px 32px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 60px;
`;

const PageTitle = styled.h1`
  font-family: ${theme.typography.fontFamily};
  font-weight: ${theme.typography.fontWeight.light};
  font-size: ${theme.typography.fontSize.xxl};
  color: white;
  margin: 0px 0 0;
  text-align: left;
  width: 90%;
  display: flex;
  align-items: center;
  height: 100%;
`;

const HeaderControls = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const SearchBar = styled.div`
  display: flex;
  align-items: center;
  background: white;
  border-radius: 8px;
  padding: 8px 16px;
  width: 300px;
`;

const SearchInput = styled.input`
  border: none;
  background: none;
  font-family: ${typography.fontFamily};
  font-size: ${typography.fontSize.base};
  width: 100%;
  padding: 0;
  margin: 0;
  outline: none;

  &::placeholder {
    color: ${colors.text.secondary};
  }
`;

const SearchIconWrapper = styled.span`
  color: ${colors.text.secondary};
  margin-right: 8px;
  display: flex;
  align-items: center;
`;

const NotificationBell = styled.div`
  color: white;
  cursor: pointer;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const NotificationDot = styled.div`
  position: absolute;
  top: -2px;
  right: -2px;
  width: 8px;
  height: 8px;
  background: #FF4444;
  border-radius: 50%;
`;

const ContentArea = styled.div`
  padding: 32px;
  max-width: 1400px;
  margin: 0 auto;
`;

const DashboardLayout = ({ children, title, currentPath }) => {
  const navigate = useNavigate();
  const [isCollapsed, setIsCollapsed] = useState(false);

  const navItems = [
    { path: '/dashboard', label: 'Overview' },
    { path: '/dashboard/company', label: 'Company Details' },
    { path: '/dashboard/policies', label: 'Policies' },
    { path: '/dashboard/claims-history', label: 'Claims History' },
    { path: '/dashboard/esg', label: 'ESG Impact' },
    { path: '/dashboard/profile', label: 'Profile' },
  ];

  return (
    <PageWrapper>
      <Sidebar isCollapsed={isCollapsed}>
        <LogoWrapper isCollapsed={isCollapsed}>
          <Logo />
        </LogoWrapper>
        <ToggleButton onClick={() => setIsCollapsed(!isCollapsed)}>
          <ToggleIcon isCollapsed={isCollapsed} />
        </ToggleButton>
        <NavMenu>
          {navItems.map((item) => (
            <NavItem
              key={item.path}
              active={currentPath === item.path}
              isCollapsed={isCollapsed}
              onClick={() => navigate(item.path)}
            >
              {!isCollapsed ? item.label : ''}
            </NavItem>
          ))}
        </NavMenu>
      </Sidebar>
      <MainContent>
        <TitleBar>
          <PageTitle>{title}</PageTitle>
          <HeaderControls>
            <SearchBar>
              <SearchIconWrapper>
                <SearchIcon />
              </SearchIconWrapper>
              <SearchInput placeholder="Search..." />
            </SearchBar>
            <NotificationBell>
              <NotificationIcon />
              <NotificationDot />
            </NotificationBell>
          </HeaderControls>
        </TitleBar>
        <ContentArea>
          {children}
        </ContentArea>
      </MainContent>
    </PageWrapper>
  );
};

export default DashboardLayout; 